import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Col, Row, View } from "@amzn/stencil-react-components/layout";
import { H3, Text } from "@amzn/stencil-react-components/text";
import IconCheckCircleFillSmall from "@amzn/stencil-react-icons/icons/icon-check-circle-fill-small";
import { FormattedMessage, useIntl } from "react-intl";

import {
  type NumberedStep,
  type PermissionStep,
  type WalkthroughPart,
  PRIVACY_AND_SECURITY_CHIP,
} from "./constants";

interface PermissionStepRowProps {
  readonly step: PermissionStep;
  /** 1-based, so the counter reads the way the design writes it. */
  readonly position: number;
  readonly total: number;
  readonly isGranted: boolean;
  /**
   * Whether this is the grant the hire is working on now. One they have not reached shows its heading
   * only, because two open walkthroughs put two numbered lists on the page.
   */
  readonly isCurrent: boolean;
  readonly onOpenSettings: () => void;
  readonly onReportIssue: () => void;
  /**
   * Points at this row's Open button, so the screen can put focus back on it.
   *
   * Only the current row is given one: a granted row has no button, and a row the hire has not reached
   * shows its heading alone.
   */
  readonly openSettingsRef?: React.Ref<HTMLButtonElement>;
}

/** One System Settings row, sitting inline with the path it illustrates. */
const PaneChip = ({ source }: { source: string }): JSX.Element => (
  <img
    src={source}
    alt=""
    aria-hidden={true}
    className="mac-permissions-chip"
  />
);

/**
 * Decorative throughout: the numbered words above each one are the accessible form.
 *
 * `isDialog` narrows the cap for a macOS alert, which the design draws far smaller than a settings pane.
 */
const Screenshot = ({
  source,
  isDialog,
}: {
  source: string;
  isDialog: boolean;
}): JSX.Element => (
  <img
    src={source}
    alt=""
    aria-hidden={true}
    className={
      isDialog
        ? "mac-permissions-walkthrough mac-permissions-walkthrough--dialog"
        : "mac-permissions-walkthrough"
    }
  />
);

/**
 * One grant: what it is, why it is needed or that it is held, and how to switch it on.
 *
 * The walkthrough shows only while the grant is outstanding and current; once given there is nothing
 * left to do, and before it is reached there is nothing to act on yet.
 */
export function PermissionStepRow({
  isCurrent,
  isGranted,
  onOpenSettings,
  onReportIssue,
  openSettingsRef,
  position,
  step,
  total,
}: PermissionStepRowProps): JSX.Element {
  const intl = useIntl();
  const paneName = intl.formatMessage({ id: step.paneNameId });
  const paneRowName = intl.formatMessage({ id: step.paneRowNameId });
  const isWalkthroughShown = !isGranted && isCurrent;

  /* Counted over the rendered parts so an inserted aside cannot shift the numbering. */
  let stepNumber = 0;

  /* Unkeyed on purpose: each returned element is the only child of an already-keyed <li>. */
  const renderPart = (part: WalkthroughPart): JSX.Element => {
    if (part.kind === "openAction") {
      return (
        <View>
          <Button
            ref={openSettingsRef}
            variant={ButtonVariant.Primary}
            onClick={onOpenSettings}
            dataTestId="macOsPermissionsOpenSettingsButton"
          >
            <FormattedMessage
              id="macPermissions-open-settings-button"
              values={{ permission: paneName }}
            />
          </Button>
        </View>
      );
    }

    if (part.kind === "manualRoute") {
      return (
        <Col gridGap="S200">
          <Text fontWeight="regular">
            <FormattedMessage
              id="macPermissions-fallback-intro"
              values={{ permission: paneName }}
            />
          </Text>
          <Text fontWeight="regular">
            <FormattedMessage
              id="macPermissions-fallback-path"
              values={{ permission: paneRowName }}
            />
          </Text>
          {/* The chips read as the path itself, so they carry the same separator as the sentence. */}
          <Row gridGap="S200" alignItems="center">
            <PaneChip source={PRIVACY_AND_SECURITY_CHIP} />
            <Text aria-hidden={true} fontWeight="regular">
              &rsaquo;
            </Text>
            <PaneChip source={step.paneChip} />
          </Row>
        </Col>
      );
    }

    if (part.kind === "note") {
      return (
        <Text fontWeight="regular">
          <FormattedMessage id={part.messageId} values={{ b: bold }} />
        </Text>
      );
    }

    /*
     * Narrowed by assignment rather than by a runtime guard: adding a kind to `WalkthroughPart` makes
     * this line fail to compile, where a `default` branch would just render the new kind as a step.
     */
    const instruction: NumberedStep = part;

    stepNumber += 1;
    return (
      <Col gridGap="S200">
        <Row gridGap="S200">
          <Text fontWeight="regular">{`${stepNumber}.`}</Text>
          <Text fontWeight="regular">
            <FormattedMessage
              id={instruction.messageId}
              values={{ b: bold, permission: paneName }}
            />
          </Text>
        </Row>
        {instruction.image !== undefined && (
          <Screenshot
            source={instruction.image}
            isDialog={instruction.isDialogImage === true}
          />
        )}
      </Col>
    );
  };

  return (
    <View className="mac-permissions-step-card">
      <Col gridGap="S200">
        <Text fontSize="T100" fontWeight="medium" color="color.text.secondary">
          <FormattedMessage
            id="macPermissions-step-counter"
            values={{ current: position, total }}
          />
        </Text>

        {/*
         * A real heading under the page's H2: this takeover is a two-section procedure, so heading
         * navigation is how a screen-reader user moves between the grants and knows which they are on.
         */}
        <H3 fontWeight="bold" fontSize="T300">
          <FormattedMessage id={step.titleId} />
        </H3>

        {isGranted && (
          <Row gridGap="S200" alignItems="center">
            <IconCheckCircleFillSmall
              aria-hidden={true}
              color="color.status.success-icon"
            />
            <Text
              fontWeight="regular"
              dataTestId={`macOsPermissionGranted-${step.reasonId}`}
            >
              <FormattedMessage id={step.grantedId} />
            </Text>
          </Row>
        )}

        {isWalkthroughShown && (
          <Col gridGap="S300">
            <Text
              fontWeight="regular"
              dataTestId={`macOsPermissionPurpose-${step.reasonId}`}
            >
              <FormattedMessage id={step.purposeId} />
            </Text>

            {/*
             * A real list, so the walkthrough is announced as one sequence and can be navigated as
             * such. Every part is an item, including the button and the aside, so the count announced
             * is of parts rather than of numbered steps -- the numbers live in the text.
             */}
            {/* The roles look redundant to the linter and are not: the stylesheet drops the list
                marker so the printed numbers are not doubled, and Safari drops the list semantics
                along with it. This is the sequence a screen-reader user navigates by. */}
            {/* eslint-disable-next-line jsx-a11y/no-redundant-roles */}
            <ol className="mac-permissions-steps" role="list">
              {step.walkthrough.map((part, order) => (
                // eslint-disable-next-line jsx-a11y/no-redundant-roles
                <li key={`${step.reasonId}-item-${order}`} role="listitem">
                  {renderPart(part)}
                </li>
              ))}
            </ol>

            <View>
              <Button
                variant={ButtonVariant.Secondary}
                onClick={onReportIssue}
                dataTestId="macOsPermissionsReportIssueLink"
              >
                <FormattedMessage id="macPermissions-report-issue-link" />
              </Button>
            </View>
          </Col>
        )}
      </Col>
    </View>
  );
}

/*
 * A plain <b>, as everywhere else in this app: a Stencil `Text` is styled as its own box, so the
 * emphasised words broke out of the sentence instead of sitting inside it.
 */
const bold = (chunks: React.ReactNode[]): JSX.Element => <b>{chunks}</b>;
